<template>
  <v-card class="base-card">
    <v-card-title class="headline accent--text pb-4"> Иностранное цитирование </v-card-title>

    <v-card-text>
      <CardFieldRow
        label="Сотрудник, отправивший  работу на проверку"
        :value="foreignCitation.commonWork.creator.fullName"
        disabled
      />

      <CardFieldRow
        label="Номер части"
        placeholder="Добавьте номер части"
        v-model="foreignCitation.partNumber"
        :disabled="isShowMode"
        :error-messages="errorMessages.partNumber"
        @input="
          $v.foreignCitation.partNumber.$touch()
          updateForeignCitation()
        "
      />

      <CardFieldRow
        label="Номер журнала"
        placeholder="Добавьте номер журнала"
        v-model="foreignCitation.magazineNumber"
        :disabled="isShowMode"
        :error-messages="errorMessages.magazineNumber"
        :required="!isShowMode"
        @input="
          $v.foreignCitation.magazineNumber.$touch()
          updateForeignCitation()
        "
      />

      <CardRow
        label="Страницы"
        :value="foreignCitation.page"
        :disabled="isShowMode"
        :required="!isShowMode"
      >
        <v-row class="mt-0 mb-0">
          <v-col
            cols="6"
            class="py-0"
          >
            <v-text-field
              v-model="foreignCitation.pageFrom"
              label="Страницы c"
              v-mask="'####'"
              outlined
              dense
              single-line
              class="rounded-lg mini-text field-edit required"
              :disabled="isShowMode"
              :error-messages="errorMessages.pageFrom"
              @input="
                $v.foreignCitation.pageFrom.$touch()
                updateForeignCitation()
              "
              @blur="$v.foreignCitation.pageFrom.$touch()"
            />
          </v-col>

          <v-col
            cols="6"
            class="py-0"
          >
            <v-text-field
              v-model="foreignCitation.pageTo"
              label="Страницы по"
              v-mask="'####'"
              outlined
              dense
              single-line
              class="rounded-lg mini-text field-edit required"
              :disabled="isShowMode"
              :error-messages="errorMessages.pageTo"
              @input="
                $v.foreignCitation.pageTo.$touch()
                updateForeignCitation()
              "
              @blur="$v.foreignCitation.pageTo.$touch()"
            />
          </v-col>
        </v-row>
      </CardRow>

      <CardFieldRow
        label="publicationOutput"
        placeholder="Добавьте publicationOutput"
        v-model="foreignCitation.publicationOutput"
        :disabled="isShowMode"
        :error-messages="errorMessages.publicationOutput"
        @input="
          $v.foreignCitation.publicationOutput.$touch()
          updateForeignCitation()
        "
      />

      <CardFieldRow
        label="Библиографическое содержание"
        placeholder="Добавьте библиографическое содержание"
        v-model="foreignCitation.fullBibliographicDescription"
        :disabled="isShowMode"
        :error-messages="errorMessages.fullBibliographicDescription"
        @input="
          $v.foreignCitation.fullBibliographicDescription.$touch()
          updateForeignCitation()
        "
      />

      <CardFieldRow
        label="DOI"
        placeholder="Добавьте DOI"
        v-model="foreignCitation.doi"
        :disabled="isShowMode"
        :error-messages="errorMessages.doi"
        @input="
          $v.foreignCitation.doi.$touch()
          updateForeignCitation()
        "
      />

      <CardFieldRow
        label="Том"
        placeholder="Добавьте том"
        v-model="foreignCitation.tome"
        :disabled="isShowMode"
        :error-messages="errorMessages.tome"
        @input="
          $v.foreignCitation.tome.$touch()
          updateForeignCitation()
        "
      />

      <CardFieldRow
        label="Название статьи"
        placeholder="Добавьте название статьи"
        v-model="foreignCitation.articleTitleEng"
        :disabled="isShowMode"
        :error-messages="errorMessages.articleTitleEng"
        :required="!isShowMode"
        @input="
          $v.foreignCitation.articleTitleEng.$touch()
          updateForeignCitation()
        "
      />

      <CardRow>
        <v-checkbox
          label="Иностранная статья"
          v-model="foreignCitation.isForeign"
          hide-details
          dense
          class="rounded-lg mini-text field-edit d-inline-block"
          :disabled="isShowMode"
          @change="updateForeignCitation()"
        />
      </CardRow>

      <CardRow>
        <v-checkbox
          label="isVac"
          v-model="foreignCitation.isVac"
          hide-details
          dense
          class="rounded-lg mini-text field-edit d-inline-block"
          :disabled="isShowMode"
          @change="updateForeignCitation()"
        />
      </CardRow>

      <CardAutocompleteRow
        label="Международный журнал"
        hide-border-bottom
        placeholder="Выберите международный журнал"
        v-model="foreignCitation.internationalMagazine"
        :items="magazineList"
        item-value="id"
        item-text="name"
        :disabled="isShowMode"
        :error-messages="errorMessages.internationalMagazine"
        @input="
          $v.foreignCitation.internationalMagazine.$touch()
          updateForeignCitation()
        "
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { ForeignCitation } from '@/models'
import CardRow from '@/components/ui/row/CardRow'
import CardFieldRow from '../../ui/row/CardFieldRow'
import { setValidateMessages } from '../../../scripts/validation'
import CardAutocompleteRow from '../../ui/row/CardAutocompleteRow'
import { required, maxLength, between } from 'vuelidate/lib/validators'
import { regexFrom1To99, regexFrom1To9999 } from '@/scripts/validation'
import { mapGetters } from 'vuex'

export default {
  components: { CardAutocompleteRow, CardFieldRow, CardRow },
  props: {
    isShowMode: Boolean,
    saveBtnLoading: Boolean,
    selectedItem: Object
  },

  data: () => ({
    foreignCitation: new ForeignCitation()
  }),

  validations() {
    return {
      foreignCitation: {
        articleTitleEng: { required, maxLength: maxLength(500) },
        internationalMagazine: { required },
        doi: { maxLength: maxLength(39) },
        tome: { maxLength: maxLength(10) },
        partNumber: { regexFrom1To99 },
        magazineNumber: { required, maxLength: maxLength(20) },

        pageFrom: { required, regexFrom1To9999 },
        pageTo: { required, regexFrom1To9999, between: between(this.foreignCitation.pageFrom || 1, 9999) },
        fullBibliographicDescription: {
          list: {
            $each: {
              bibliography: { required },
              link: { required, maxLength: maxLength(1000) }
            }
          }
        }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['magazineList']),

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.foreignCitation.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.foreignCitation[key])
      })

      return allErrors
    }
  },

  methods: {
    updateForeignCitation() {
      this.$emit('update:selected-item', new ForeignCitation(this.foreignCitation.getForAPI()))
      this.updateInvalid()
    },

    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.foreignCitation = new ForeignCitation(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
